import React, { useEffect, useState } from "react";
import api from "../../api";
import { Loader2, Plus, Trash2, Pencil, X, Tag } from "lucide-react";
import { toast } from "../../hooks/use-toast";

const empty = { code: "", type: "percent", value: "", max_uses: 0, active: true, expires_at: "" };

export default function DiscountsTab() {
  const [items, setItems] = useState(null);
  const [modal, setModal] = useState(null);

  const load = () => api.get("/business/discounts").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...modal, value: parseFloat(modal.value) || 0, max_uses: parseInt(modal.max_uses) || 0 };
    try {
      if (modal.id) await api.put(`/business/discounts/${modal.id}`, payload);
      else await api.post("/business/discounts", payload);
      toast({ title: "Saved", description: "Discount code saved." });
      setModal(null); load();
    } catch (err) {
      toast({ title: "Error", description: err?.response?.data?.detail || "Failed", variant: "destructive" });
    }
  };

  const toggle = async (d) => {
    await api.put(`/business/discounts/${d.id}`, { ...d, active: !d.active });
    load();
  };

  const del = async (id) => { await api.delete(`/business/discounts/${id}`); toast({ title: "Deleted" }); load(); };

  if (!items) return <Loader2 className="animate-spin text-[#2ec9c9]" size={32} />;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-[#5a6b80] max-w-[520px]">Promo codes give buyers money off the purchase price while the recipient still gets the full voucher value — perfect for seasonal offers.</p>
        <button onClick={() => setModal({ ...empty })} className="gp-btn-teal rounded-full px-5 py-2.5 text-sm font-medium flex items-center gap-2 shrink-0">
          <Plus size={18} /> New code
        </button>
      </div>

      {items.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center text-[#8a97a8] shadow-[0_8px_24px_rgba(20,30,55,0.06)]">No discount codes yet.</div>
      ) : (
        <div className="bg-white rounded-2xl shadow-[0_8px_24px_rgba(20,30,55,0.06)] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#f6f8fb] text-[#8a97a8]">
              <tr>{["Code", "Discount", "Uses", "Status", ""].map((h) => <th key={h} className="text-left px-5 py-3 font-medium">{h}</th>)}</tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.id} className="border-t border-gray-50">
                  <td className="px-5 py-3 font-mono font-semibold text-[#16243b] flex items-center gap-2"><Tag size={14} className="text-[#2ec9c9]" />{d.code}</td>
                  <td className="px-5 py-3 text-[#5a6b80]">{d.type === "percent" ? `${d.value}% off` : `£${d.value.toFixed(2)} off`}</td>
                  <td className="px-5 py-3 text-[#5a6b80]">{d.used_count}{d.max_uses ? ` / ${d.max_uses}` : " / ∞"}{d.expires_at && <span className={`block text-xs ${new Date(d.expires_at) < new Date() ? "text-red-500" : "text-[#8a97a8]"}`} data-testid={`discount-expiry-${d.id}`}>{new Date(d.expires_at) < new Date() ? "Expired" : "Expires"} {d.expires_at.slice(0, 10)}</span>}</td>
                  <td className="px-5 py-3">
                    <button onClick={() => toggle(d)} className={`px-2.5 py-1 rounded-full text-xs font-medium ${d.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      {d.active ? "Active" : "Off"}
                    </button>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setModal({ ...d, value: String(d.value), max_uses: d.max_uses })} className="text-[#5a6b80] hover:text-[#16243b]"><Pencil size={16} /></button>
                      <button onClick={() => del(d.id)} className="text-red-400 hover:text-red-600"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-[420px]" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-5 border-b border-gray-100">
              <h3 className="font-bold text-[#16243b]">{modal.id ? "Edit code" : "New discount code"}</h3>
              <button onClick={() => setModal(null)}><X className="text-[#5a6b80]" /></button>
            </div>
            <form onSubmit={save} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#16243b] mb-1.5">Code</label>
                <input value={modal.code} onChange={(e) => setModal({ ...modal, code: e.target.value.toUpperCase() })} required placeholder="SUMMER20"
                  className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] uppercase font-mono" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-[#16243b] mb-1.5">Type</label>
                  <select value={modal.type} onChange={(e) => setModal({ ...modal, type: e.target.value })}
                    className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] bg-white">
                    <option value="percent">% off</option>
                    <option value="fixed">£ off</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#16243b] mb-1.5">Value</label>
                  <input type="number" step="0.01" value={modal.value} onChange={(e) => setModal({ ...modal, value: e.target.value })} required
                    className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#16243b] mb-1.5">Max uses (0 = unlimited)</label>
                <input type="number" min="0" value={modal.max_uses} onChange={(e) => setModal({ ...modal, max_uses: e.target.value })}
                  className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#16243b] mb-1.5">Expiry date (optional)</label>
                <input type="date" value={(modal.expires_at || "").slice(0, 10)} data-testid="discount-expires-input"
                  onChange={(e) => setModal({ ...modal, expires_at: e.target.value ? new Date(`${e.target.value}T23:59:00`).toISOString() : "" })}
                  className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
              </div>
              <label className="flex items-center gap-2 text-sm text-[#16243b]">
                <input type="checkbox" checked={modal.active} onChange={(e) => setModal({ ...modal, active: e.target.checked })} /> Active
              </label>
              <button className="gp-btn-teal w-full rounded-xl py-3 font-medium">Save code</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
